import React from "react";
import { useDelete, usePost } from "../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { styled } from "@linaria/react";
import { fonts } from "@ui";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useRevalidate } from "../../../api/useQuery";

interface P {
  closeModal: () => void;
  isActive: boolean;
  analysisId: string;
  projectId: string;
  compliant: boolean;
}

const ToggleActiveModalForm = ({ closeModal, isActive, analysisId, projectId, compliant }: P) => {
  const revalidate = useRevalidate();
  const activateAnalysis = usePost(
    "/projects/:projectId(uuid)/analyses/:analysisId(uuid)/activation",
  );
  const deactivateAnalysis = useDelete(
    "/projects/:projectId(uuid)/analyses/:analysisId(uuid)/activation",
  );

  const { addToast } = useToasts();
  const handleSubmit = React.useCallback(async () => {
    if (isActive) {
      await deactivateAnalysis({
        routeParams: { projectId, analysisId },
      });
      addToast("success", t("measures.chart.analysis.modal.deactivate.success"));
    } else {
      await activateAnalysis({
        routeParams: { projectId, analysisId },
      });
      addToast("success", t("measures.chart.analysis.modal.activate.success"));
    }

    void revalidate("/projects/:projectId(uuid)/setups", { projectId });

    return true;
  }, [isActive, projectId, revalidate, deactivateAnalysis, analysisId, addToast, activateAnalysis]);

  return (
    <ModalForm
      cancellable
      onRequestClose={closeModal}
      onSubmit={handleSubmit}
      okButtonVariant={isActive ? "destructive" : "primary"}
      okButtonName={t("measures.chart.analysis.modal.toggle.analysis", { isActive })}
    >
      {isActive ? (
        <StyledText>{t("measures.chart.analysis.modal.deactivate.description")}</StyledText>
      ) : (
        <>
          <StyledText>{t("measures.chart.analysis.modal.activate.description")}</StyledText>
          {compliant ? null : (
            <AdditionnalMessage
              status="WARNING"
              text={t("measures.chart.analysis.modal.activate.warning.description")}
            />
          )}
        </>
      )}
    </ModalForm>
  );
};

export default React.memo(ToggleActiveModalForm);

//#region CSS
const StyledText = styled.div`
  font: ${fonts.body1};
`;
//#endregion CSS
